import React, { useState, useEffect } from 'react';
import {
  Modal,
  Steps,
  Form,
  Input,
  Button,
  Select,
  message,
  Card,
  Typography,
  Space,
  Row,
  Col,
  InputNumber,
  Collapse,
  Descriptions,
  Divider,
  Badge,
  Upload,
  DatePicker
} from 'antd';
import {
  CloseOutlined,
  PlusOutlined,
  MinusOutlined,
  CheckOutlined,
  BankOutlined,
  SettingOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { Lead } from '../services/api';
import apiService from '../services/api';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import moment from 'moment';
import { extractLeadFields } from '../utils/fieldMapping';

const { Step } = Steps;
const { Option } = Select;
const { Title, Text } = Typography;
const { Panel } = Collapse;

interface EnergyProposalWizardProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  lead: Lead | null;
  marcacaoId?: number | null;
}

interface EmpresaData {
  nomeEmpresa?: string;
  nifEmpresa?: string;
  moradaFornecimento?: string;
  moradaCorrespondencia?: string;
  regiao?: string;
  cae?: string;
  nomeRepresentante?: string;
  contactoRepresentante?: string;
  emailRepresentante?: string;
  emailEmpresa?: string;
}

interface ServiceInstance {
  instanceId: string;
  service: string;
  instanceNumber: number;
}

interface ServiceDetails {
  [instanceId: string]: any;
}

const SERVICE_NAMES: Record<string, string> = {
  'bairro-solar': 'Bairro Solar',
  'pap': 'Pay AS Produced (PAP)',
  'transacional': 'Transacional',
  'luz': 'Luz',
  'gas': 'Gás',
  'mobilidade': 'Mobilidade Elétrica',
  'outro': 'Outro'
};

const EnergyProposalWizard: React.FC<EnergyProposalWizardProps> = ({ 
  open, 
  onClose, 
  onSave, 
  lead,
  marcacaoId 
}) => {
  const { user } = useCognitoAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  
  // Form data
  const [empresa, setEmpresa] = useState<EmpresaData>({});
  const [serviceQuantities, setServiceQuantities] = useState<Record<string, number>>({
    'bairro-solar': 0,
    'pap': 0,
    'transacional': 0,
    'luz': 0,
    'gas': 0,
    'mobilidade': 0,
    'outro': 0
  });
  const [serviceInstances, setServiceInstances] = useState<ServiceInstance[]>([]);
  const [serviceDetails, setServiceDetails] = useState<ServiceDetails>({});
  const [expandedSegments, setExpandedSegments] = useState<Record<string, boolean>>({});
  
  // Validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const steps = [
    {
      title: 'Empresa',
      description: 'Informações da empresa cliente',
      icon: <BankOutlined />
    },
    {
      title: 'Serviço',
      description: 'Selecione os serviços e configure os detalhes',
      icon: <SettingOutlined />
    },
    {
      title: 'Resumo',
      description: 'Revisão de todas as informações',
      icon: <FileTextOutlined />
    }
  ];

  // Load saved data when modal opens
  useEffect(() => {
    const loadSavedData = async () => {
      if (open && lead?.id) {
        try {
          setLoading(true);
          const proposals = await apiService.getEnergyProposalsByLead(lead.id);
          
          if (proposals && Array.isArray(proposals) && proposals.length > 0) {
            // Use the most recent proposal (first in array)
            const latestProposal = proposals[0];
            
            // Load empresa data
            if (latestProposal.empresa_data) {
              const empresaData = typeof latestProposal.empresa_data === 'string' 
                ? JSON.parse(latestProposal.empresa_data) 
                : latestProposal.empresa_data;
              setEmpresa(empresaData);
              // Update form fields
              form.setFieldsValue(empresaData);
            }
            
            // Load service quantities
            if (latestProposal.service_quantities) {
              const quantities = typeof latestProposal.service_quantities === 'string'
                ? JSON.parse(latestProposal.service_quantities)
                : latestProposal.service_quantities;
              setServiceQuantities(quantities);
            }
            
            // Load service instances
            if (latestProposal.service_instances) {
              const instances = typeof latestProposal.service_instances === 'string'
                ? JSON.parse(latestProposal.service_instances)
                : latestProposal.service_instances;
              setServiceInstances(instances);
            }
            
            // Load service details (handle file paths - they're stored as paths, not File objects)
            if (latestProposal.service_details) {
              const details = typeof latestProposal.service_details === 'string'
                ? JSON.parse(latestProposal.service_details)
                : latestProposal.service_details;
              
              // Process details to handle file paths
              const processedDetails: ServiceDetails = {};
              Object.keys(details).forEach(instanceId => {
                processedDetails[instanceId] = { ...details[instanceId] };
                // File paths are stored, but we'll show the filename if available
                // The actual file download will use the download_url from the API
              });
              
              setServiceDetails(processedDetails);
            }
          } else {
            // No saved data, autofill from lead data
            setCurrentStep(0);
            const leadFields = extractLeadFields(lead);
            const empresaData: EmpresaData = {
              nomeEmpresa: leadFields?.company || leadFields?.name || '',
              nifEmpresa: leadFields?.nif || '',
              moradaFornecimento: leadFields?.address || '',
              moradaCorrespondencia: leadFields?.address || '',
              regiao: leadFields?.city || '',
              nomeRepresentante: leadFields?.name || '',
              contactoRepresentante: leadFields?.phone || '',
              emailRepresentante: leadFields?.email || '',
              emailEmpresa: leadFields?.email || '',
            };
            setEmpresa(empresaData);
            form.setFieldsValue(empresaData);
            setServiceQuantities({
              'bairro-solar': 0,
              'pap': 0,
              'transacional': 0,
              'luz': 0,
              'gas': 0,
              'mobilidade': 0,
              'outro': 0
            });
            setServiceInstances([]);
            setServiceDetails({});
            setExpandedSegments({});
            setFieldErrors({});
          }
        } catch (error) {
          console.error('Error loading saved proposal:', error);
          // On error, autofill from lead data
          setCurrentStep(0);
          const leadFields = extractLeadFields(lead);
          const empresaData: EmpresaData = {
            nomeEmpresa: leadFields?.company || leadFields?.name || '',
            nifEmpresa: leadFields?.nif || '',
            moradaFornecimento: leadFields?.address || '',
            moradaCorrespondencia: leadFields?.address || '',
            regiao: leadFields?.city || '',
            nomeRepresentante: leadFields?.name || '',
            contactoRepresentante: leadFields?.phone || '',
            emailRepresentante: leadFields?.email || '',
            emailEmpresa: leadFields?.email || '',
          };
          setEmpresa(empresaData);
          form.setFieldsValue(empresaData);
          setServiceQuantities({
            'bairro-solar': 0,
            'pap': 0,
            'transacional': 0,
            'luz': 0,
            'gas': 0,
            'mobilidade': 0,
            'outro': 0
          });
          setServiceInstances([]);
          setServiceDetails({});
          setExpandedSegments({});
          setFieldErrors({});
        } finally {
          setLoading(false);
        }
      } else if (open && lead) {
        // No lead ID but lead object exists, autofill from lead data
        setCurrentStep(0);
        const leadFields = extractLeadFields(lead);
        const empresaData: EmpresaData = {
          nomeEmpresa: leadFields?.company || leadFields?.name || '',
          nifEmpresa: leadFields?.nif || '',
          moradaFornecimento: leadFields?.address || '',
          moradaCorrespondencia: leadFields?.address || '',
          regiao: leadFields?.city || '',
          nomeRepresentante: leadFields?.name || '',
          contactoRepresentante: leadFields?.phone || '',
          emailRepresentante: leadFields?.email || '',
          emailEmpresa: leadFields?.email || '',
        };
        setEmpresa(empresaData);
        form.setFieldsValue(empresaData);
        setServiceQuantities({
          'bairro-solar': 0,
          'pap': 0,
          'transacional': 0,
          'luz': 0,
          'gas': 0,
          'mobilidade': 0,
          'outro': 0
        });
        setServiceInstances([]);
        setServiceDetails({});
        setExpandedSegments({});
        setFieldErrors({});
        form.resetFields();
      }
    };

    loadSavedData();
  }, [open, lead?.id, form]);

  // Update service instances when quantities change
  useEffect(() => {
    const instances: ServiceInstance[] = [];
    Object.entries(serviceQuantities).forEach(([service, quantity]) => {
      for (let i = 1; i <= quantity; i++) {
        instances.push({
          instanceId: `${service}-${i}`,
          service,
          instanceNumber: i
        });
      }
    });
    setServiceInstances(instances);
  }, [serviceQuantities]);

  const handleServiceQuantityChange = (service: string, quantity: number) => {
    const newQuantities = { ...serviceQuantities, [service]: Math.max(0, Math.min(99, quantity)) };
    setServiceQuantities(newQuantities);
    
    // Remove service details for instances that no longer exist
    const newDetails = { ...serviceDetails };
    Object.keys(newDetails).forEach(key => {
      if (key.startsWith(`${service}-`) && parseInt(key.split('-')[1]) > quantity) {
        delete newDetails[key];
      }
    });
    setServiceDetails(newDetails);
  };

  const handleEmpresaFieldChange = (field: keyof EmpresaData, value: string) => {
    setEmpresa(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNifInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 9);
    handleEmpresaFieldChange('nifEmpresa', value);
  };

  const handleNifPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 9);
    handleEmpresaFieldChange('nifEmpresa', pasted);
  };

  const handleCaeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    handleEmpresaFieldChange('cae', value);
  };

  const handleCaePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
    handleEmpresaFieldChange('cae', pasted);
  };

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    if (step === 0) {
      if (!empresa.nomeEmpresa?.trim()) {
        errors.nomeEmpresa = 'Campo obrigatório';
      }
      if (!empresa.nifEmpresa || empresa.nifEmpresa.length !== 9) {
        errors.nifEmpresa = 'NIF deve ter 9 dígitos';
      }
      if (!empresa.moradaFornecimento?.trim()) {
        errors.moradaFornecimento = 'Campo obrigatório';
      }
      if (!empresa.regiao?.trim()) {
        errors.regiao = 'Campo obrigatório';
      }
      if (!empresa.cae || empresa.cae.length !== 5) {
        errors.cae = 'CAE deve ter 5 dígitos';
      }
      if (!empresa.nomeRepresentante?.trim()) {
        errors.nomeRepresentante = 'Campo obrigatório';
      }
      if (!empresa.contactoRepresentante?.trim()) {
        errors.contactoRepresentante = 'Campo obrigatório';
      }
      if (!empresa.emailRepresentante?.trim()) {
        errors.emailRepresentante = 'Campo obrigatório';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(empresa.emailRepresentante)) {
        errors.emailRepresentante = 'Email inválido';
      }
    } else if (step === 1) {
      const totalServices = Object.values(serviceQuantities).reduce((sum, qty) => sum + qty, 0);
      if (totalServices === 0) {
        errors.servicos = 'Selecione pelo menos um serviço';
      }
      
      // Validate service details for each instance
      serviceInstances.forEach(instance => {
        const instanceId = instance.instanceId;
        const details = serviceDetails[instanceId] || {};
        
        if (instance.service === 'gas') {
          // Validate CUI for gas service
          if (!details.cui || !details.cui.match(/^PT\d{11}$/i)) {
            errors[`cui-${instanceId}`] = 'CUI deve ter o formato PT + 11 dígitos (ex: PT12345678901)';
          }
          // Validate Escalão
          if (!details.escalao || !['1', '2', '3', '4'].includes(details.escalao)) {
            errors[`escalao-${instanceId}`] = 'Selecione um escalão';
          }
          // Validate Fatura
          if (!details.fatura) {
            errors[`fatura-${instanceId}`] = 'Fatura é obrigatória';
          }
        } else if (instance.service === 'bairro-solar' || instance.service === 'transacional' || instance.service === 'pap' || instance.service === 'luz') {
          // Validate CPE
          if (!details.cpe || !details.cpe.match(/^PT\d{16}[A-Z]{2}$/i)) {
            errors[`cpe-${instanceId}`] = 'CPE deve ter o formato PT + 16 dígitos + 2 letras (ex: PT1234567890123456AB)';
          }
          // Validate Potência
          if (!details.potencia || !['btn', 'bte', 'mt', 'outra'].includes(details.potencia)) {
            errors[`potencia-${instanceId}`] = 'Selecione uma potência';
          }
          if (details.potencia === 'outra' && !details.potenciaOutra?.trim()) {
            errors[`potenciaOutra-${instanceId}`] = 'Especifique a potência';
          }
          // Validate Fatura
          if (!details.fatura) {
            errors[`fatura-${instanceId}`] = 'Fatura é obrigatória';
          }
          // Validate Print do Telhado for specific services
          if ((instance.service === 'bairro-solar' || instance.service === 'transacional' || instance.service === 'pap') && !details.printTelhado) {
            errors[`printTelhado-${instanceId}`] = 'Print do Telhado é obrigatório';
          }
          // Validate Coordenadas Geográficas for Bairro Solar
          if (instance.service === 'bairro-solar' && !details.coordenadasGeograficas?.trim()) {
            errors[`coordenadasGeograficas-${instanceId}`] = 'Coordenadas Geográficas são obrigatórias';
          }
          // Validate Forma de Pagamento for Transacional
          if (instance.service === 'transacional' && !details.formaPagamento) {
            errors[`formaPagamento-${instanceId}`] = 'Selecione a forma de pagamento';
          }
        }
      });
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const toggleServiceSegment = (segmentId: string) => {
    setExpandedSegments(prev => {
      const currentState = prev[segmentId] || false;
      return {
        ...prev,
        [segmentId]: !currentState
      };
    });
  };

  const handleServiceDetailChange = (instanceId: string, field: string, value: any) => {
    setServiceDetails(prev => ({
      ...prev,
      [instanceId]: {
        ...prev[instanceId],
        [field]: value
      }
    }));
  };

  const handleCpeInput = (instanceId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();
    // Remove non-alphanumeric except PT prefix
    if (value.startsWith('PT')) {
      value = 'PT' + value.slice(2).replace(/[^0-9A-Z]/g, '');
    } else {
      value = value.replace(/[^0-9A-Z]/g, '');
      if (value.length > 0 && !value.startsWith('PT')) {
        value = 'PT' + value;
      }
    }
    // Limit to PT + 16 digits + 2 letters (20 chars total)
    if (value.length > 20) value = value.slice(0, 20);
    handleServiceDetailChange(instanceId, 'cpe', value);
  };

  const handleCpePaste = (instanceId: string, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').toUpperCase().replace(/[^0-9A-Z]/g, '');
    let value = pasted.startsWith('PT') ? pasted : 'PT' + pasted;
    if (value.length > 20) value = value.slice(0, 20);
    handleServiceDetailChange(instanceId, 'cpe', value);
  };

  const handleCuiInput = (instanceId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();
    // Remove non-alphanumeric except PT prefix
    if (value.startsWith('PT')) {
      value = 'PT' + value.slice(2).replace(/[^0-9]/g, '');
    } else {
      value = value.replace(/[^0-9A-Z]/g, '');
      if (value.length > 0 && !value.startsWith('PT')) {
        value = 'PT' + value;
      }
    }
    // Limit to PT + 11 digits (13 chars total)
    if (value.length > 13) value = value.slice(0, 13);
    handleServiceDetailChange(instanceId, 'cui', value);
  };

  const handleCuiPaste = (instanceId: string, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').toUpperCase().replace(/[^0-9]/g, '');
    let value = pasted.startsWith('PT') ? pasted : 'PT' + pasted;
    if (value.length > 13) value = value.slice(0, 13);
    handleServiceDetailChange(instanceId, 'cui', value);
  };

  const handleServiceFileChange = (instanceId: string, field: string, file: File | null) => {
    if (file) {
      handleServiceDetailChange(instanceId, field, file);
      handleServiceDetailChange(instanceId, `${field}_name`, file.name);
    } else {
      handleServiceDetailChange(instanceId, field, null);
      handleServiceDetailChange(instanceId, `${field}_name`, '');
    }
  };

  const copyToClipboard = (text: string, fieldName: string) => {
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        message.success(`${fieldName} copiado para a área de transferência!`);
      }).catch(() => {
        message.error('Erro ao copiar');
      });
    }
  };

  const renderCopyButtons = (instanceId: string, field: string, fieldName: string) => {
    const value = serviceDetails[instanceId]?.[field] || '';
    if (!value) return null;
    return (
      <Button
        type="text"
        size="small"
        icon={<CopyOutlined />}
        onClick={() => copyToClipboard(String(value), fieldName)}
        style={{ marginBottom: 8, padding: '4px 8px', height: 'auto' }}
      >
        Copiar {fieldName}
      </Button>
    );
  };

  const createServiceSegment = (instance: ServiceInstance) => {
    const instanceId = instance.instanceId;
    const isExpanded = expandedSegments[instanceId] || false;
    const serviceName = SERVICE_NAMES[instance.service] || instance.service;
    const segmentTitle = `${serviceName} - ${instance.instanceNumber}`;
    const details = serviceDetails[instanceId] || {};

    return (
      <div
        key={instanceId}
        style={{
          marginBottom: 20,
          borderRadius: '12px',
          border: '2px solid #e5e7eb',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          background: '#ffffff'
        }}
      >
        <div
          onClick={() => toggleServiceSegment(instanceId)}
          style={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            background: '#1890ff',
            color: 'white',
            fontWeight: 600
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#096dd9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#1890ff';
          }}
        >
          <Text strong style={{ fontSize: '15px', color: 'white' }}>{segmentTitle}</Text>
          <Text style={{ fontSize: '12px', color: 'white' }}>{isExpanded ? '▼' : '▶'}</Text>
        </div>
        {isExpanded ? (
          <div style={{ padding: '20px', background: '#ffffff' }}>
            <Row gutter={[16, 20]}>
              {instance.service === 'gas' ? (
                <>
                  <Col span={24}>
                    <Form.Item 
                      label={<span style={{ fontSize: '14px', fontWeight: 500 }}>CUI <span style={{ color: '#ff4d4f' }}>*</span></span>}
                      validateStatus={fieldErrors[`cui-${instanceId}`] ? 'error' : ''}
                      help={fieldErrors[`cui-${instanceId}`]}
                    >
                      {renderCopyButtons(instanceId, 'cui', 'CUI')}
                      <Input
                        size="large"
                        placeholder="PT + 11 dígitos (ex: PT12345678901)"
                        maxLength={13}
                        value={details.cui || ''}
                        onChange={(e) => handleCuiInput(instanceId, e)}
                        onPaste={(e) => handleCuiPaste(instanceId, e)}
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          padding: '16px 20px',
                          fontSize: '16px',
                          textTransform: 'uppercase'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#1890ff';
                          e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item 
                      label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Escalão <span style={{ color: '#ff4d4f' }}>*</span></span>}
                      validateStatus={fieldErrors[`escalao-${instanceId}`] ? 'error' : ''}
                      help={fieldErrors[`escalao-${instanceId}`]}
                    >
                      <Space>
                        <Button
                          type={details.escalao === '1' ? 'primary' : 'default'}
                          onClick={() => handleServiceDetailChange(instanceId, 'escalao', details.escalao === '1' ? '' : '1')}
                          style={{ borderRadius: '8px' }}
                        >
                          Escalão 1
                        </Button>
                        <Button
                          type={details.escalao === '2' ? 'primary' : 'default'}
                          onClick={() => handleServiceDetailChange(instanceId, 'escalao', details.escalao === '2' ? '' : '2')}
                          style={{ borderRadius: '8px' }}
                        >
                          Escalão 2
                        </Button>
                        <Button
                          type={details.escalao === '3' ? 'primary' : 'default'}
                          onClick={() => handleServiceDetailChange(instanceId, 'escalao', details.escalao === '3' ? '' : '3')}
                          style={{ borderRadius: '8px' }}
                        >
                          Escalão 3
                        </Button>
                        <Button
                          type={details.escalao === '4' ? 'primary' : 'default'}
                          onClick={() => handleServiceDetailChange(instanceId, 'escalao', details.escalao === '4' ? '' : '4')}
                          style={{ borderRadius: '8px' }}
                        >
                          Escalão 4
                        </Button>
                      </Space>
                    </Form.Item>
                  </Col>
                </>
              ) : (instance.service === 'bairro-solar' || instance.service === 'transacional' || instance.service === 'pap' || instance.service === 'luz') ? (
                <>
                  <Col span={24}>
                    <Form.Item 
                      label={<span style={{ fontSize: '14px', fontWeight: 500 }}>CPE <span style={{ color: '#ff4d4f' }}>*</span></span>}
                      validateStatus={fieldErrors[`cpe-${instanceId}`] ? 'error' : ''}
                      help={fieldErrors[`cpe-${instanceId}`]}
                    >
                      {renderCopyButtons(instanceId, 'cpe', 'CPE')}
                      <Input
                        size="large"
                        placeholder="PT + 16 dígitos + 2 letras (ex: PT1234567890123456AB)"
                        maxLength={20}
                        value={details.cpe || ''}
                        onChange={(e) => handleCpeInput(instanceId, e)}
                        onPaste={(e) => handleCpePaste(instanceId, e)}
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          padding: '16px 20px',
                          fontSize: '16px',
                          textTransform: 'uppercase'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#1890ff';
                          e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Potência <span style={{ color: '#ff4d4f' }}>*</span></span>}>
                      <Space>
                        <Button
                          type={details.potencia === 'btn' ? 'primary' : 'default'}
                          onClick={() => handleServiceDetailChange(instanceId, 'potencia', details.potencia === 'btn' ? '' : 'btn')}
                          style={{ borderRadius: '8px' }}
                        >
                          BTN
                        </Button>
                        <Button
                          type={details.potencia === 'bte' ? 'primary' : 'default'}
                          onClick={() => handleServiceDetailChange(instanceId, 'potencia', details.potencia === 'bte' ? '' : 'bte')}
                          style={{ borderRadius: '8px' }}
                        >
                          BTE
                        </Button>
                        <Button
                          type={details.potencia === 'mt' ? 'primary' : 'default'}
                          onClick={() => handleServiceDetailChange(instanceId, 'potencia', details.potencia === 'mt' ? '' : 'mt')}
                          style={{ borderRadius: '8px' }}
                        >
                          MT
                        </Button>
                        <Button
                          type={details.potencia === 'outra' ? 'primary' : 'default'}
                          onClick={() => handleServiceDetailChange(instanceId, 'potencia', details.potencia === 'outra' ? '' : 'outra')}
                          style={{ borderRadius: '8px' }}
                        >
                          Outra
                        </Button>
                      </Space>
                      {details.potencia === 'outra' && (
                        <div style={{ marginTop: 16 }}>
                          {renderCopyButtons(instanceId, 'potenciaOutra', 'Potência')}
                          <Input
                            size="large"
                            placeholder="Especifique outra potência"
                            value={details.potenciaOutra || ''}
                            onChange={(e) => handleServiceDetailChange(instanceId, 'potenciaOutra', e.target.value)}
                            style={{
                              borderRadius: '12px',
                              border: '2px solid #e5e7eb',
                              padding: '16px 20px',
                              fontSize: '16px'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#1890ff';
                              e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e5e7eb';
                              e.target.style.boxShadow = 'none';
                            }}
                          />
                        </div>
                      )}
                    </Form.Item>
                  </Col>
                </>
              ) : null}
              {(instance.service === 'gas' || instance.service === 'bairro-solar' || instance.service === 'transacional' || instance.service === 'pap' || instance.service === 'luz') && (
                <>
                  <Col span={24}>
                    <Form.Item label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Fatura <span style={{ color: '#ff4d4f' }}>*</span></span>}>
                      <Upload
                        beforeUpload={() => false}
                        onChange={(info) => {
                          const file = info.file.originFileObj || info.file as any;
                          handleServiceFileChange(instanceId, 'fatura', file);
                        }}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        showUploadList={false}
                      >
                        <Button icon={<UploadOutlined />} style={{ borderRadius: '8px' }}>
                          Selecionar Fatura
                        </Button>
                      </Upload>
                      {details.fatura_name && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '12px' }}>
                          {details.fatura_name}
                        </Text>
                      )}
                    </Form.Item>
                  </Col>
                  {(instance.service === 'bairro-solar' || instance.service === 'transacional' || instance.service === 'pap') && (
                    <Col span={24}>
                      <Form.Item label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Print do Telhado <span style={{ color: '#ff4d4f' }}>*</span></span>}>
                        <Upload
                          beforeUpload={() => false}
                          onChange={(info) => {
                            const file = info.file.originFileObj || info.file as any;
                            handleServiceFileChange(instanceId, 'printTelhado', file);
                          }}
                          accept=".jpg,.jpeg,.png"
                          showUploadList={false}
                        >
                          <Button icon={<UploadOutlined />} style={{ borderRadius: '8px' }}>
                            Selecionar Print do Telhado
                          </Button>
                        </Upload>
                        {details.printTelhado_name && (
                          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '12px' }}>
                            {details.printTelhado_name}
                          </Text>
                        )}
                      </Form.Item>
                    </Col>
                  )}
                  {instance.service === 'bairro-solar' && (
                    <Col span={24}>
                      <Form.Item 
                        label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Coordenadas Geográficas <span style={{ color: '#ff4d4f' }}>*</span></span>}
                        validateStatus={fieldErrors[`coordenadasGeograficas-${instanceId}`] ? 'error' : ''}
                        help={fieldErrors[`coordenadasGeograficas-${instanceId}`]}
                      >
                        {renderCopyButtons(instanceId, 'coordenadasGeograficas', 'Coordenadas Geográficas')}
                        <Input.TextArea
                          rows={3}
                          placeholder="Coordenadas geográficas"
                          value={details.coordenadasGeograficas || ''}
                          onChange={(e) => handleServiceDetailChange(instanceId, 'coordenadasGeograficas', e.target.value)}
                          style={{
                            borderRadius: '12px',
                            border: '2px solid #e5e7eb',
                            padding: '16px 20px',
                            fontSize: '16px'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#1890ff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {instance.service === 'transacional' && (
                    <Col span={24}>
                      <Form.Item label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Forma de Pagamento <span style={{ color: '#ff4d4f' }}>*</span></span>}>
                        <Space>
                          <Button
                            type={details.formaPagamento === 'pronto' ? 'primary' : 'default'}
                            onClick={() => handleServiceDetailChange(instanceId, 'formaPagamento', details.formaPagamento === 'pronto' ? '' : 'pronto')}
                            style={{ borderRadius: '8px' }}
                          >
                            Pronto Pagamento
                          </Button>
                          <Button
                            type={details.formaPagamento === 'prestacoes' ? 'primary' : 'default'}
                            onClick={() => handleServiceDetailChange(instanceId, 'formaPagamento', details.formaPagamento === 'prestacoes' ? '' : 'prestacoes')}
                            style={{ borderRadius: '8px' }}
                          >
                            Prestações
                          </Button>
                        </Space>
                      </Form.Item>
                    </Col>
                  )}
                  <Col span={24}>
                    <Form.Item label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Observações</span>}>
                      {renderCopyButtons(instanceId, 'observacoes', 'Observações')}
                      <Input.TextArea
                        rows={3}
                        placeholder="Observações adicionais"
                        value={details.observacoes || ''}
                        onChange={(e) => handleServiceDetailChange(instanceId, 'observacoes', e.target.value)}
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          padding: '16px 20px',
                          fontSize: '16px'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#1890ff';
                          e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              {(instance.service === 'mobilidade' || instance.service === 'outro') && (
                <Col span={24}>
                  <Form.Item label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Observações</span>}>
                    {renderCopyButtons(instanceId, 'observacoes', 'Observações')}
                    <Input.TextArea
                      rows={3}
                      placeholder="Observações sobre este serviço"
                      value={details.observacoes || ''}
                      onChange={(e) => handleServiceDetailChange(instanceId, 'observacoes', e.target.value)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        padding: '16px 20px',
                        fontSize: '16px'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1890ff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
              </Row>
            </div>
          ) : null}
      </div>
    );
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) {
      setCurrentStep(1);
      return;
    }

    setLoading(true);
    try {
      // Prepare submission data with all fields
      const submissionData = {
        lead_id: lead?.id || null,
        marcacao_id: marcacaoId || null,
        empresa: empresa,
        service_quantities: serviceQuantities,
        service_instances: serviceInstances,
        service_details: serviceDetails
      };

      // Save proposal to database
      await apiService.saveEnergyProposal(submissionData);
      
      message.success('Proposta enviada com sucesso!');
      if (onSave) {
        onSave();
      }
      onClose();
    } catch (error: any) {
      console.error('Error submitting energy proposal:', error);
      message.error(error?.response?.data?.error || error?.message || 'Erro ao submeter proposta');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Card
            style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8'
            }}
            bodyStyle={{ padding: '32px' }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={4} style={{ margin: 0, color: '#000000', fontSize: '20px', fontWeight: 600 }}>
                  <BankOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  Informações da Empresa
                </Title>
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '14px' }}>
                  Preencha os dados da empresa cliente
                </Text>
              </div>
              <Divider style={{ margin: '24px 0' }} />
              <Row gutter={[16, 20]}>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Nome da Empresa <span style={{ color: '#ff4d4f' }}>*</span></span>}
                  validateStatus={fieldErrors.nomeEmpresa ? 'error' : ''}
                  help={fieldErrors.nomeEmpresa}
                >
                  <Input
                    size="large"
                    placeholder="Nome da empresa"
                    value={empresa.nomeEmpresa}
                    onChange={(e) => handleEmpresaFieldChange('nomeEmpresa', e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '16px 20px',
                      fontSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ fontSize: '14px', fontWeight: 500 }}>NIF da Empresa <span style={{ color: '#ff4d4f' }}>*</span></span>}
                  validateStatus={fieldErrors.nifEmpresa ? 'error' : ''}
                  help={fieldErrors.nifEmpresa}
                >
                  <Input
                    size="large"
                    placeholder="Número de identificação fiscal"
                    maxLength={9}
                    value={empresa.nifEmpresa}
                    onChange={handleNifInput}
                    onPaste={handleNifPaste}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '16px 20px',
                      fontSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Morada de Fornecimento <span style={{ color: '#ff4d4f' }}>*</span></span>}
                  validateStatus={fieldErrors.moradaFornecimento ? 'error' : ''}
                  help={fieldErrors.moradaFornecimento}
                >
                  <Input
                    size="large"
                    placeholder="Morada completa de fornecimento"
                    value={empresa.moradaFornecimento}
                    onChange={(e) => handleEmpresaFieldChange('moradaFornecimento', e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '16px 20px',
                      fontSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Morada de Correspondência</span>}>
                  <Input
                    size="large"
                    placeholder="Morada de correspondência (opcional)"
                    value={empresa.moradaCorrespondencia}
                    onChange={(e) => handleEmpresaFieldChange('moradaCorrespondencia', e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '16px 20px',
                      fontSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Localidade <span style={{ color: '#ff4d4f' }}>*</span></span>}
                  validateStatus={fieldErrors.regiao ? 'error' : ''}
                  help={fieldErrors.regiao}
                >
                  <Input
                    size="large"
                    placeholder="Localidade"
                    value={empresa.regiao}
                    onChange={(e) => handleEmpresaFieldChange('regiao', e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '16px 20px',
                      fontSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ fontSize: '14px', fontWeight: 500 }}>CAE <span style={{ color: '#ff4d4f' }}>*</span></span>}
                  validateStatus={fieldErrors.cae ? 'error' : ''}
                  help={fieldErrors.cae}
                >
                  <Input
                    size="large"
                    placeholder="Código de atividade económica"
                    maxLength={5}
                    value={empresa.cae}
                    onChange={handleCaeInput}
                    onPaste={handleCaePaste}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '16px 20px',
                      fontSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Nome do Representante <span style={{ color: '#ff4d4f' }}>*</span></span>}
                  validateStatus={fieldErrors.nomeRepresentante ? 'error' : ''}
                  help={fieldErrors.nomeRepresentante}
                >
                  <Input
                    size="large"
                    placeholder="Nome completo do representante"
                    value={empresa.nomeRepresentante}
                    onChange={(e) => handleEmpresaFieldChange('nomeRepresentante', e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '16px 20px',
                      fontSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Contacto do Representante <span style={{ color: '#ff4d4f' }}>*</span></span>}
                  validateStatus={fieldErrors.contactoRepresentante ? 'error' : ''}
                  help={fieldErrors.contactoRepresentante}
                >
                  <Input
                    size="large"
                    placeholder="Número de telefone"
                    value={empresa.contactoRepresentante}
                    onChange={(e) => handleEmpresaFieldChange('contactoRepresentante', e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '16px 20px',
                      fontSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Email do Representante <span style={{ color: '#ff4d4f' }}>*</span></span>}
                  validateStatus={fieldErrors.emailRepresentante ? 'error' : ''}
                  help={fieldErrors.emailRepresentante}
                >
                  <Input
                    size="large"
                    type="email"
                    placeholder="Email do representante"
                    value={empresa.emailRepresentante}
                    onChange={(e) => handleEmpresaFieldChange('emailRepresentante', e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '16px 20px',
                      fontSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Email da Empresa</span>}>
                  <Input
                    size="large"
                    type="email"
                    placeholder="Email geral da empresa"
                    value={empresa.emailEmpresa}
                    onChange={(e) => handleEmpresaFieldChange('emailEmpresa', e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '16px 20px',
                      fontSize: '16px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            </Space>
          </Card>
        );

      case 1:
        return (
          <Card
            style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8'
            }}
            bodyStyle={{ padding: '32px' }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={4} style={{ margin: 0, color: '#000000', fontSize: '20px', fontWeight: 600 }}>
                  <SettingOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  Seleção de Serviços
                </Title>
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '14px' }}>
                  Selecione os serviços e configure os detalhes
                </Text>
              </div>
              <Divider style={{ margin: '24px 0' }} />
              <Form.Item
                label={<span style={{ fontSize: '15px', fontWeight: 500 }}>Serviços <span style={{ color: '#ff4d4f' }}>*</span></span>}
                validateStatus={fieldErrors.servicos ? 'error' : ''}
                help={fieldErrors.servicos}
                style={{ marginBottom: 0 }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {Object.entries(SERVICE_NAMES).map(([key, name]) => (
                    <Card
                      key={key}
                      size="small"
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        transition: 'all 0.3s ease',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#1890ff';
                        e.currentTarget.style.backgroundColor = '#e6f7ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.backgroundColor = '#ffffff';
                      }}
                      hoverable
                    >
                      <Row align="middle" justify="space-between">
                        <Col>
                          <Space>
                            {(serviceQuantities[key] || 0) > 0 && (
                              <Badge count={serviceQuantities[key]} style={{ backgroundColor: '#1890ff' }} />
                            )}
                            <Text strong style={{ fontSize: '15px' }}>{name}</Text>
                          </Space>
                        </Col>
                        <Col>
                          <Space>
                            <Button
                              icon={<MinusOutlined />}
                              onClick={() => handleServiceQuantityChange(key, (serviceQuantities[key] || 0) - 1)}
                              disabled={(serviceQuantities[key] || 0) === 0}
                              size="small"
                              style={{
                                width: '40px',
                                height: '40px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            />
                            <InputNumber
                              min={0}
                              max={99}
                              value={serviceQuantities[key] || 0}
                              onChange={(value) => handleServiceQuantityChange(key, value || 0)}
                              style={{
                                width: '65px',
                                height: '40px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                textAlign: 'center',
                                fontWeight: 600,
                                fontSize: '16px'
                              }}
                              size="small"
                              onFocus={(e) => {
                                e.target.style.borderColor = '#1890ff';
                                e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                            <Button
                              icon={<PlusOutlined />}
                              onClick={() => handleServiceQuantityChange(key, (serviceQuantities[key] || 0) + 1)}
                              size="small"
                              style={{
                                width: '40px',
                                height: '40px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            />
                          </Space>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </Space>
              </Form.Item>

              {serviceInstances.length > 0 && (
                <div style={{ marginTop: 32 }}>
                  <Divider style={{ margin: '24px 0' }} />
                  <Title level={5} style={{ marginBottom: 16, color: '#262626', fontSize: '16px', fontWeight: 600 }}>
                    Detalhes do Serviço
                  </Title>
                  {serviceInstances.map(instance => createServiceSegment(instance))}
                </div>
              )}
            </Space>
          </Card>
        );

      case 2:
        return (
          <Card
            style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8'
            }}
            bodyStyle={{ padding: '32px' }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={4} style={{ margin: 0, color: '#000000', fontSize: '20px', fontWeight: 600 }}>
                  <FileTextOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  Resumo da Proposta
                </Title>
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '14px' }}>
                  Revisão de todas as informações antes de submeter
                </Text>
              </div>
              <Divider style={{ margin: '24px 0' }} />
              <div style={{
                background: '#f8f9fa',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px'
              }}>
              <div style={{
                marginBottom: '20px',
                padding: '15px',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1890ff',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '12px' }}>●</span> Empresa
                </h4>
                <Descriptions column={1} size="small" style={{ marginTop: '10px' }}>
                  <Descriptions.Item label="Nome da Empresa">
                    {empresa.nomeEmpresa || <Text type="danger">-</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="NIF">
                    {empresa.nifEmpresa || <Text type="danger">-</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Morada de Fornecimento">
                    {empresa.moradaFornecimento || <Text type="danger">-</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Morada de Correspondência">
                    {empresa.moradaCorrespondencia || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Localidade">
                    {empresa.regiao || <Text type="danger">-</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="CAE">
                    {empresa.cae || <Text type="danger">-</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nome do Representante">
                    {empresa.nomeRepresentante || <Text type="danger">-</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Contacto do Representante">
                    {empresa.contactoRepresentante || <Text type="danger">-</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email do Representante">
                    {empresa.emailRepresentante || <Text type="danger">-</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email da Empresa">
                    {empresa.emailEmpresa || '-'}
                  </Descriptions.Item>
                </Descriptions>
              </div>

              <div style={{
                marginBottom: '20px',
                padding: '15px',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1890ff',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '12px' }}>●</span> Serviço
                </h4>
                {serviceInstances.length > 0 ? (
                  <div>
                    {serviceInstances.map(instance => {
                      const serviceName = SERVICE_NAMES[instance.service] || instance.service;
                      const instanceId = instance.instanceId;
                      const details = serviceDetails[instanceId] || {};
                      return (
                        <div key={instanceId} style={{ marginBottom: 20, padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>{serviceName} - {instance.instanceNumber}</Text>
                          <Descriptions column={1} size="small" style={{ marginTop: '10px' }}>
                            {instance.service === 'gas' ? (
                              <>
                                <Descriptions.Item label="CUI">
                                  {details.cui || <Text type="danger">-</Text>}
                                </Descriptions.Item>
                                <Descriptions.Item label="Escalão">
                                  {details.escalao ? `Escalão ${details.escalao}` : <Text type="danger">-</Text>}
                                </Descriptions.Item>
                                <Descriptions.Item label="Fatura">
                                  {details.fatura_name || <Text type="danger">-</Text>}
                                </Descriptions.Item>
                                {details.observacoes && (
                                  <Descriptions.Item label="Observações">
                                    {details.observacoes}
                                  </Descriptions.Item>
                                )}
                              </>
                            ) : (instance.service === 'bairro-solar' || instance.service === 'transacional' || instance.service === 'pap' || instance.service === 'luz') ? (
                              <>
                                <Descriptions.Item label="CPE">
                                  {details.cpe || <Text type="danger">-</Text>}
                                </Descriptions.Item>
                                <Descriptions.Item label="Potência">
                                  {details.potencia === 'outra' 
                                    ? (details.potenciaOutra || <Text type="danger">-</Text>)
                                    : (details.potencia ? details.potencia.toUpperCase() : <Text type="danger">-</Text>)
                                  }
                                </Descriptions.Item>
                                <Descriptions.Item label="Fatura">
                                  {details.fatura_name || <Text type="danger">-</Text>}
                                </Descriptions.Item>
                                {(instance.service === 'bairro-solar' || instance.service === 'transacional' || instance.service === 'pap') && (
                                  <Descriptions.Item label="Print do Telhado">
                                    {details.printTelhado_name || <Text type="danger">-</Text>}
                                  </Descriptions.Item>
                                )}
                                {instance.service === 'bairro-solar' && (
                                  <Descriptions.Item label="Coordenadas Geográficas">
                                    {details.coordenadasGeograficas || <Text type="danger">-</Text>}
                                  </Descriptions.Item>
                                )}
                                {instance.service === 'transacional' && (
                                  <Descriptions.Item label="Forma de Pagamento">
                                    {details.formaPagamento === 'pronto' ? 'Pronto Pagamento' : details.formaPagamento === 'prestacoes' ? 'Prestações' : <Text type="danger">-</Text>}
                                  </Descriptions.Item>
                                )}
                                {details.observacoes && (
                                  <Descriptions.Item label="Observações">
                                    {details.observacoes}
                                  </Descriptions.Item>
                                )}
                              </>
                            ) : (
                              <>
                                {details.observacoes && (
                                  <Descriptions.Item label="Observações">
                                    {details.observacoes}
                                  </Descriptions.Item>
                                )}
                                {!details.observacoes && (
                                  <Descriptions.Item label="Observações">
                                    <Text type="secondary">-</Text>
                                  </Descriptions.Item>
                                )}
                              </>
                            )}
                          </Descriptions>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Text type="danger">-</Text>
                )}
              </div>
            </div>
            </Space>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1000}
      footer={null}
      closeIcon={
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            color: 'white',
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 1000
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
        >
          <CloseOutlined style={{ fontSize: '14px', color: 'white' }} />
        </div>
      }
      bodyStyle={{
        padding: '0',
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto'
      }}
      style={{
        borderRadius: '8px'
      }}
      wrapClassName="energy-proposal-modal"
      title={null}
    >
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
        color: 'white',
        padding: '30px 32px',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        boxShadow: '0 4px 20px rgba(24, 144, 255, 0.3)',
        position: 'relative'
      }}>
        <Title level={3} style={{ margin: 0, color: 'white', fontSize: '28px', fontWeight: 700, lineHeight: 1.2 }}>
          Solicitar Proposta Energia/Solar
        </Title>
        <Text style={{ fontSize: '14px', marginTop: '8px', display: 'block', color: 'white', opacity: 0.9 }}>
          Preencha os dados para solicitar uma proposta detalhada
        </Text>
      </div>

      {/* Content Container */}
      <div style={{ padding: '32px' }}>
      <div style={{ 
        marginBottom: 40,
        padding: '24px',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '12px',
        border: '1px solid #bae7ff'
      }}>
        <Steps
          current={currentStep}
          style={{ padding: '0' }}
          items={steps.map((step, index) => {
            const isActive = currentStep === index;
            const isCompleted = currentStep > index;
            return {
              key: index,
              title: (
                <span style={{ 
                  fontSize: '15px', 
                  fontWeight: isActive ? 700 : isCompleted ? 600 : 400,
                  color: isActive ? '#1890ff' : isCompleted ? '#52c41a' : '#8c8c8c',
                  transition: 'all 0.3s ease'
                }}>
                  {step.title}
                </span>
              ),
              description: (
                <span style={{ 
                  fontSize: '13px', 
                  color: isActive ? '#1890ff' : isCompleted ? '#52c41a' : '#8c8c8c',
                  fontWeight: isActive ? 500 : 400,
                  transition: 'all 0.3s ease'
                }}>
                  {step.description}
                </span>
              ),
              icon: (
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: isActive 
                    ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)' 
                    : isCompleted 
                    ? 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)'
                    : '#f0f0f0',
                  color: isActive || isCompleted ? 'white' : '#8c8c8c',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  fontWeight: 600,
                  boxShadow: isActive 
                    ? '0 4px 12px rgba(24, 144, 255, 0.3)' 
                    : isCompleted
                    ? '0 4px 12px rgba(82, 196, 26, 0.3)'
                    : '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  border: isActive ? '3px solid #91d5ff' : isCompleted ? '3px solid #b7eb8f' : '3px solid transparent'
                }}>
                  {step.icon}
                </div>
              ),
              status: isCompleted ? 'finish' : isActive ? 'process' : 'wait'
            };
          })}
        />
      </div>

      <Form form={form} layout="vertical">
        {renderStepContent()}
      </Form>

      <Divider style={{ margin: '32px 0 24px 0', borderColor: '#e5e7eb', borderWidth: '2px' }} />
      <div style={{
        marginTop: '40px',
        padding: '25px 0',
        borderTop: '2px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '20px'
      }}>
        <Button
          onClick={currentStep > 0 ? handlePrev : onClose}
          size="large"
          style={{
            minWidth: '140px',
            padding: '14px 32px',
            height: 'auto',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 600,
            background: 'white',
            color: '#333333',
            border: '2px solid #e5e7eb',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f9fafb';
            e.currentTarget.style.borderColor = '#d1d5db';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.08)';
          }}
        >
          {currentStep > 0 ? '← Anterior' : 'Cancelar'}
        </Button>
        <Button
          type="primary"
          onClick={currentStep < steps.length - 1 ? handleNext : handleSubmit}
          loading={loading}
          size="large"
          icon={currentStep === steps.length - 1 ? <CheckCircleOutlined /> : undefined}
          style={{
            minWidth: '140px',
            padding: '14px 32px',
            height: 'auto',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
            border: 'none',
            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(24, 144, 255, 0.4)';
            e.currentTarget.style.background = 'linear-gradient(135deg, #096dd9 0%, #0050b3 100%)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.3)';
            e.currentTarget.style.background = 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)';
          }}
        >
          {currentStep < steps.length - 1 ? 'Seguinte →' : '✓ Submeter Proposta'}
        </Button>
      </div>
      </div>
    </Modal>
  );
};

export default EnergyProposalWizard;

